import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import {
  type AcuantApplicationDetailDto,
  type AcuantApplicationSearchResultDto,
  type AcuantDocumentsDto,
  type AcuantHistoryEntryDto,
  type AcuantNoteDto,
  type AcuantSecurityTestDto,
  type AcuantSignedUrlDto,
  type AcuantTransactionDto
} from '../../api-schema-types/kyc-archive/KycArchiveDtos'

export interface KycArchiveSearchRequest {
  q: string
  page: number
  pageSize: number
}

export const kycArchiveApi = createApi({
  reducerPath: 'kycArchiveApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/kyc-archive`,
    prepareHeaders: (headers: Headers) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  endpoints: (builder) => ({
    searchApplications: builder.query<AcuantApplicationSearchResultDto, KycArchiveSearchRequest>({
      query: (search) => ({
        url: '/applications-search',
        method: 'GET',
        params: search
      })
    }),

    getApplication: builder.query<AcuantApplicationDetailDto, string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}`,
        method: 'GET'
      })
    }),

    getTransactions: builder.query<AcuantTransactionDto[], string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}/transactions`,
        method: 'GET'
      })
    }),

    getSecurityTests: builder.query<AcuantSecurityTestDto[], string>({
      query: (transactionId) => ({
        url: `/transactions/${transactionId}/security-tests`,
        method: 'GET'
      })
    }),

    getHistory: builder.query<AcuantHistoryEntryDto[], string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}/history`,
        method: 'GET'
      })
    }),

    getNotes: builder.query<AcuantNoteDto[], string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}/notes`,
        method: 'GET'
      })
    }),

    getDocuments: builder.query<AcuantDocumentsDto, string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}/documents`,
        method: 'GET'
      })
    }),

    getDocumentUrl: builder.query<AcuantSignedUrlDto, number>({
      query: (documentImageId) => ({
        url: `/documents/${documentImageId}/url`,
        method: 'GET'
      })
    }),

    getAttachmentUrl: builder.query<AcuantSignedUrlDto, number>({
      query: (attachmentId) => ({
        url: `/attachments/${attachmentId}/url`,
        method: 'GET'
      })
    })
  })
})

export const useSearchKycArchiveQuery = kycArchiveApi.endpoints.searchApplications.useQuery
export const useGetKycArchiveApplicationQuery = kycArchiveApi.endpoints.getApplication.useQuery
export const useGetKycArchiveTransactionsQuery = kycArchiveApi.endpoints.getTransactions.useQuery
export const useGetKycArchiveSecurityTestsQuery = kycArchiveApi.endpoints.getSecurityTests.useQuery
export const useGetKycArchiveHistoryQuery = kycArchiveApi.endpoints.getHistory.useQuery
export const useGetKycArchiveNotesQuery = kycArchiveApi.endpoints.getNotes.useQuery
export const useGetKycArchiveDocumentsQuery = kycArchiveApi.endpoints.getDocuments.useQuery
export const useGetKycArchiveDocumentUrlLazyQuery = kycArchiveApi.endpoints.getDocumentUrl.useLazyQuery
export const useGetKycArchiveAttachmentUrlLazyQuery = kycArchiveApi.endpoints.getAttachmentUrl.useLazyQuery
